import { useEffect, useRef } from 'react';
import styles from 'components/Pages/FundsCenter/FundDetails/FundDetailsPerformanceTabCumulativeSection/Chart.module.scss';
import useHelpers from 'hooks/helpers';
import { useI18n } from 'next-localization';
import * as Plot from '@observablehq/plot';

type Data = {
  label: string;
  period: string;
  values: {
    basisPoint: string;
    date: string;
  }[];
};

type Props = {
  data: Array<Data>;
  className: string;
};

type ConvertedDate = {
  date: Date;
  value: number;
};

const FundDetailsPremiumDiscountLineChart = ({ data, className }: Props) => {
  const { localizeDate } = useHelpers();
  const { t } = useI18n();
  const chartWrapper = useRef(null);

  useEffect(() => {
    if (!data) return;

    const convertedData: Array<ConvertedDate> = data
      .flatMap((point) => point.values)
      .map((d) => ({
        date: new Date(d.date),
        value: Number(d.basisPoint),
      }));

    const minValue = Math.min(...convertedData.map((d) => d.value));
    const plot: any = Plot.plot({
      y: {
        grid: true,
        label: t('fundDetailsPremiumDiscountBps'),
      },
      marginTop: 40,
      marginBottom: 40,
      width: chartWrapper.current?.offsetWidth,
      height: chartWrapper.current?.offsetHeight,
      marks: [
        Plot.ruleY([minValue]),

        Plot.areaY(convertedData, {
          x: 'date',
          y1: minValue,
          y2: 'value',
          fill: '#00205B',
          opacity: 0.05,
        }),

        Plot.lineY(convertedData, {
          x: 'date',
          y: 'value',
          stroke: '#00205B',
          strokeWidth: 2,
        }),

        Plot.ruleX(
          convertedData,
          Plot.pointerX({
            x: 'date',
            strokeWidth: 1,
            opacity: 0.2,
            maxRadius: 999,
          })
        ),

        Plot.dot(
          convertedData,
          Plot.pointerX({
            x: 'date',
            y: 'value',
            fill: '#00205B',
            r: 4,
            strokeWidth: 0,
            maxRadius: 999,
          })
        ),

        Plot.tip(
          convertedData,
          Plot.pointerX({
            x: 'date',
            y: 'value',
            title: (d) => `${t('date')}: ${localizeDate(d.date)}\n${t('value')}: ${d.value}`,
            strokeWidth: 0,
            pointerSize: 0,
            textPadding: 12,
            fontSize: 12,
            lineHeight: 1.5,
            maxRadius: 999,
          })
        ),
      ],
    });
    chartWrapper.current.append(plot);

    return () => plot.remove();
  }, [data]);
  return (
    <div className={`position-relative ${className}`}>
      <div ref={chartWrapper} className={styles['chart-wrapper']} />
    </div>
  );
};

export default FundDetailsPremiumDiscountLineChart;
